import React from 'react';

import { FileInfo, Vedlegg, VedleggStatus } from '../filopplasting-types';
import { allowedFiles, STORE_FILTYPER } from './filtyper-blacklist';

import './file-validation.scss';

const HAR_ALLEREDE_VALGT_FIL = 'harAlleredeValgtFil';
const FOR_STOR_FIL = 'forStorFil';
const FOR_LITEN_FIL = 'forLitenFil';
const FEIL_FILTYPE = 'feilFiltype';
const FOR_STORE_FILER_TOTALT = 'forStoreFilerTotalt';
const FILE_FEIL = 'fileFeil';

const ONE_MEGABYTE = 1024 * 1024;
export const MAX_FILE_SIZE_BYTES = 8 * ONE_MEGABYTE;
export const MAX_TOTAL_FILE_SIZE_BYTES = 25 * ONE_MEGABYTE;

interface Varsellinje {
    filnavn: string[];
    varseltekst: (arg: string[]) => string;
}

export interface Varslinger {
    [HAR_ALLEREDE_VALGT_FIL]: Varsellinje;
    [FOR_STOR_FIL]: Varsellinje;
    [FOR_LITEN_FIL]: Varsellinje;
    [FEIL_FILTYPE]: Varsellinje;
    [FOR_STORE_FILER_TOTALT]: Varsellinje;
    [FILE_FEIL]: Varsellinje;
}

type VarslingKeys = keyof Varslinger;

const fileToLarge = (fil: File): boolean => {
    return fil.size >= MAX_FILE_SIZE_BYTES;
};

const fileIsDuplicate = (fil: File, godkjenteFiler: Vedlegg[]): boolean => {
    return godkjenteFiler.some((dok) => dok.fileInfo.name === fil.name);
};

const getFiltype = (fil: FileInfo) => {
    const startindexFiltype = fil && fil.name.lastIndexOf('.');
    return startindexFiltype && fil.name.substr(startindexFiltype + 1, fil.name.length - 1);
};

const isGodkjentFiltype = (fil: FileInfo) => {
    return allowedFiles.includes(getFiltype(fil).toLowerCase());
};

const isForLitenFil = (fil: FileInfo) => {
    return STORE_FILTYPER.includes(getFiltype(fil)) ? fil.size < 1024 : fil.size < 1;
};

export const calculateFileSize = (fileSizes: number[]) => fileSizes.reduce((a, b) => a + b, 0);

const totalFileSize = (valgteFiler: FileInfo[], godkjenteFiler: Vedlegg[]) =>
    calculateFileSize(godkjenteFiler.map((tmp) => tmp.fileInfo.size)) +
    calculateFileSize(valgteFiler.map((tmp) => tmp.size));

export const validerValgteFiler = (valgteFiler: any, godkjenteFiler: Vedlegg[]) => {
    return valgteFiler.filter((fil: FileInfo) => {
        return !(
            godkjenteFiler.find((dok) => dok.fileInfo.name === fil.name) ||
            fil.size >= MAX_FILE_SIZE_BYTES ||
            isForLitenFil(fil) ||
            !isGodkjentFiltype(fil) ||
            totalFileSize(valgteFiler, godkjenteFiler) > MAX_TOTAL_FILE_SIZE_BYTES
        );
    });
};

const feilFileOrFieles = (filnavn: string[]) => {
    return filnavn.length > 1
        ? `Kunne ikke laste opp filene: ${filnavn.join(', ')}.`
        : `Kunne ikke laste opp fil: ${filnavn.join(', ')}.`;
};

export const defaultVarslinger = (): Varslinger => {
    return {
        [HAR_ALLEREDE_VALGT_FIL]: {
            filnavn: [],
            varseltekst: (filnavn) =>
                filnavn.length > 1
                    ? `Du har lastet opp disse filene fra før: ${filnavn.join(', ')}.`
                    : `Du har lastet opp ${filnavn.join(', ')} fra før.`,
        },
        [FOR_STOR_FIL]: {
            filnavn: [],
            varseltekst: (filnavn) =>
                filnavn.length > 1
                    ? `Filene ${filnavn.join(', ')} er for store. Maks filstørrelse er 8 MB.`
                    : `Filen ${filnavn.join(', ')} er for stor. Maks filstørrelse er 8 MB.`,
        },
        [FOR_LITEN_FIL]: {
            filnavn: [],
            varseltekst: (filnavn) =>
                filnavn.length > 1
                    ? `Filene ${filnavn.join(', ')} er for små. 
                    Minimum filstørrelse er 1 kb for typene pdf, png, jpg, jpeg, doc, docx, bmp og 1 byte for andre.`
                    : `Filen ${filnavn.join(', ')} er for liten. 
                    Minimum filstørrelse er 1 kb for typene pdf, png, jpg, jpeg, doc, docx, bmp og 1 byte for andre.`,
        },
        [FEIL_FILTYPE]: {
            filnavn: [],
            varseltekst: (filnavn) =>
                filnavn.length > 1
                    ? `Støtter ikke filtypene til følgende filer: ${filnavn.join(', ')}.`
                    : `Støtter ikke filtypen til ${filnavn.join(', ')}.`,
        },
        [FOR_STORE_FILER_TOTALT]: {
            filnavn: [],
            varseltekst: (filnavn) =>
                `Kunne ikke laste opp filene: ${filnavn.join(
                    ', ',
                )}. \n\nTotal filstørrelse overstiger grensen på 25 MB.\n\nHvis vi trenger flere dokumenter enn det du har lastet opp, tar vi kontakt med deg.`,
        },
        [FILE_FEIL]: {
            filnavn: [],
            varseltekst: (filnavn) =>
                `${feilFileOrFieles(
                    filnavn,
                )} Prøv å laste opp vedlegget på nytt. \n\nDersom problemet gjentar seg flere ganger, kan du sende inn skademeldingen uten vedlegg, og så hjelper vi deg videre.`,
        },
    };
};

export const getVarslinger = (valgteFiler: FileInfo[], godkjenteFiler: Vedlegg[], isUpload = true) => {
    const nyeVarslinger: Varslinger = defaultVarslinger();
    valgteFiler.forEach((fil: File) => {
        if (fileToLarge(fil)) nyeVarslinger[FOR_STOR_FIL].filnavn.push(fil.name);
        if (isUpload && fileIsDuplicate(fil, godkjenteFiler))
            nyeVarslinger[HAR_ALLEREDE_VALGT_FIL].filnavn.push(fil.name);
        if (isForLitenFil(fil)) nyeVarslinger[FOR_LITEN_FIL].filnavn.push(fil.name);
        if (!isGodkjentFiltype(fil)) nyeVarslinger[FEIL_FILTYPE].filnavn.push(fil.name);
    });

    godkjenteFiler.forEach((f) => {
        if (f.status === VedleggStatus.Error) nyeVarslinger[FILE_FEIL].filnavn.push(f.fileName);
        if (f.status === VedleggStatus.ErrorTotalFileSize) {
            nyeVarslinger[FOR_STORE_FILER_TOTALT].filnavn.push(f.fileName);
        }
    });

    if (isUpload && totalFileSize(valgteFiler, godkjenteFiler) > MAX_TOTAL_FILE_SIZE_BYTES)
        nyeVarslinger[FOR_STORE_FILER_TOTALT].filnavn.push(...valgteFiler.map((fil) => fil.name));
    return nyeVarslinger;
};

interface Props {
    varslinger: Varslinger;
}

const FileValidation = ({ varslinger }: Props) => {
    const aktiverteVarsler = (varslinger: Varslinger) => {
        return Object.keys(varslinger)
            .map((varselType) => varselType as VarslingKeys)
            .filter((varselType) => varslinger[varselType].filnavn.length > 0)
            .map((varselType) => varslinger[varselType]);
    };

    const aktiveVarsler = aktiverteVarsler(varslinger);

    return (
        <>
            {aktiveVarsler.length > 0 && (
                <span className="sgw-label error">
                    <span className="sgw-icon">
                        <span className="icon icon--24 icon--utropstegn-24" />
                    </span>
                    {aktiveVarsler.length > 0 && (
                        <span>
                            <span data-testid="filvalidering__header" className="varselboks__header">
                                Følgende feil skjedde under opplasting av filer:
                            </span>
                            <ul className="varselboks__list">
                                {aktiveVarsler.map((varsel) => (
                                    <li
                                        className="varselboks__list_item"
                                        key={varsel.filnavn.join() + varsel.varseltekst}
                                    >
                                        {varsel.varseltekst(varsel.filnavn)}
                                    </li>
                                ))}
                            </ul>
                        </span>
                    )}
                </span>
            )}
        </>
    );
};

export default FileValidation;
